import { useState, useCallback } from 'react';
import { DeviceData } from '../../DevicesTab';

interface DeviceTypeFilters {
  cameras: boolean;
  speakers: boolean;
  relays: boolean;
}

export const useSearchAndFilters = () => {
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [deviceTypeFilters, setDeviceTypeFilters] = useState<DeviceTypeFilters>({
    cameras: true,
    speakers: true,
    relays: true
  });

  const handleSearchChange = useCallback((term: string) => {
    setGlobalSearchTerm(term);
  }, []);

  const handleFilterChange = useCallback((filterType: keyof DeviceTypeFilters, checked: boolean) => {
    setDeviceTypeFilters(prev => ({ ...prev, [filterType]: checked }));
  }, []);

  const filterCameras = useCallback((cameras: any[]) => {
    const searchTerm = globalSearchTerm.toLowerCase();
    
    return cameras.filter(camera => {
      // If cameras are unchecked in filters, don't apply search filtering
      if (!deviceTypeFilters.cameras) return true;
      
      // If no search term, show all cameras
      if (!searchTerm) return true;
      
      // Apply search filtering
      return camera.name?.toLowerCase().includes(searchTerm) ||
             camera.assetName?.toLowerCase().includes(searchTerm) ||
             camera.id?.toLowerCase().includes(searchTerm);
    });
  }, [globalSearchTerm, deviceTypeFilters.cameras]);

  const filterDevicesByType = useCallback(<T extends DeviceData>(
    devices: T[], 
    deviceType: keyof DeviceTypeFilters
  ): T[] => {
    const searchTerm = globalSearchTerm.toLowerCase();
    
    return devices.filter(device => {
      // If device type is unchecked in filters, don't apply search filtering
      if (!deviceTypeFilters[deviceType]) return true;
      
      // If no search term, show all devices
      if (!searchTerm) return true;
      
      // Apply search filtering
      return device.name?.toLowerCase().includes(searchTerm) ||
             device.assetName?.toLowerCase().includes(searchTerm) ||
             device.id?.toLowerCase().includes(searchTerm);
    });
  }, [globalSearchTerm, deviceTypeFilters]);

  return {
    globalSearchTerm,
    isSearchExpanded,
    deviceTypeFilters,
    handleSearchChange,
    handleFilterChange,
    setIsSearchExpanded,
    filterCameras,
    filterDevicesByType
  };
};